import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { http, getDeviceId, getToken, setToken, clearToken } from "@/lib/api";
import * as off from "@/lib/offline";
import { tickReminder } from "@/lib/reminders";
import { captureFromUrl } from "@/lib/attribution";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

export function AppProvider({ children }) {
  const [content, setContent] = useState(null);
  const [contentError, setContentError] = useState(false);
  const [progress, setProgress] = useState({ count: 0, needed: 12, unlocked: false });
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [installEvt, setInstallEvt] = useState(null);
  const [focusMode, setFocusMode] = useState(false);
  const deviceId = getDeviceId();

  const refreshProgress = useCallback(async () => {
    try {
      const { data } = await http.get(`/progress/${deviceId}`);
      setProgress(data);
    } catch (e) { /* offline / transient */ }
  }, [deviceId]);

  const loadContent = useCallback(() => {
    setContentError(false);
    http.get("/content")
      .then(({ data }) => setContent(data))
      .catch(() => { if (!off.getLib()) setContentError(true); });
  }, []);

  const loadMe = useCallback(async () => {
    if (!getToken()) { setUser(null); setAuthReady(true); return; }
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
    } catch (e) { clearToken(); setUser(null); }
    setAuthReady(true);
  }, []);

  const sync = useCallback(async () => {
    if (off.outboxCount() > 0) {
      const n = await off.flushOutbox();
      if (n > 0) refreshProgress();
    }
  }, [refreshProgress]);

  useEffect(() => {
    captureFromUrl();
    loadContent();
    off.loadLib();
    refreshProgress();
    loadMe();
    sync();
    tickReminder(() =>
      toast("A gentle moment to check in", {
        description: "Whenever you're ready — there's no streak to keep.",
        action: { label: "Check in", onClick: () => { window.location.href = "/navigate"; } },
      })
    );

    const goOnline = () => { setOnline(true); loadContent(); off.loadLib(); sync(); };
    const goOffline = () => setOnline(false);
    const onInstall = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    window.addEventListener("beforeinstallprompt", onInstall);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("beforeinstallprompt", onInstall);
    };
  }, [loadContent, refreshProgress, loadMe, sync]);

  const requestLink = async (email) => (await http.post("/auth/request-link", { email, app_url: window.location.origin })).data;

  const verify = async (token) => {
    const { data } = await http.post("/auth/verify", { token, device_id: deviceId });
    setToken(data.token);
    setUser(data.user);
    await refreshProgress();
    return data;
  };

  const logout = async () => {
    try { await http.post("/auth/logout"); } catch (e) {}
    clearToken();
    localStorage.removeItem("ss_seen_mission");
    localStorage.removeItem("ss_seen_intro");
    setUser(null);
    await refreshProgress();
  };

  const checkoutPlus = async (lookupKey) => {
    const { data } = await http.post("/payments/checkout", {
      lookup_key: lookupKey, origin_url: window.location.origin,
    });
    window.location.href = data.checkout_url;
  };

  const openBillingPortal = async () => {
    const { data } = await http.post("/billing/portal", { origin_url: window.location.origin });
    window.location.href = data.url;
  };

  const promptInstall = async () => {
    if (!installEvt) return false;
    installEvt.prompt();
    const { outcome } = await installEvt.userChoice;
    setInstallEvt(null);
    return outcome === "accepted";
  };

  return (
    <AppCtx.Provider value={{
      content, contentError, reloadContent: loadContent, deviceId, progress, refreshProgress,
      user, authReady, isAdmin: user?.role === "admin", isPlus: user?.plan === "plus",
      reloadUser: loadMe, checkoutPlus, openBillingPortal,
      requestLink, verify, logout,
      online, canInstall: !!installEvt, promptInstall,
      focusMode, setFocusMode,
    }}>
      {children}
    </AppCtx.Provider>
  );
}
